"use client";

import { FC, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { captureException } from "@sentry/react";
import Image from "next/image";
import { useRouter } from "next/router";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import { QuestionIcon } from "components/atoms/Icon";
import { Input } from "components/atoms/Input";
import { Toggle } from "components/atoms/Toggle";
import { ReferTooltip } from "components/molecules/Tooltip";
import { checkUsernameAvailability, getCurrentUserInfo, updateUser } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, IUserDTO } from "types";
import { colourScheme } from "utils/styling/colours";
import { Small, Span } from "utils/styling/typography.styled";
import { STRIPE_CUSTOMER_PORTAL } from "utils/urlConstants";
import * as Styled from "./Profile.styled";
interface UserUpdates {
  username?: string;
  marketingConsent?: boolean;
}
export const ProfilePage: FC = () => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const router = useRouter();
  const [user, setUser] = useState<IUserDTO | undefined>();
  const [isUsernameValid, setIsUsernameValid] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [newUsername, setNewUsername] = useState<string>("");
  const [newMarketingConsent, setNewMarketingConsent] = useState<boolean>(false);
  const [changesMade, setChangesMade] = useState<boolean>(false);
  const handleMySubscriptionClick = () => {
    window.open(STRIPE_CUSTOMER_PORTAL + "?prefilled_email=" + user?.email, "_blank");
  };
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const {
          user
        } = await getCurrentUserInfo();
        if (user) {
          setUser(user);
          setNewUsername(user.username);
          setNewMarketingConsent(user?.marketingConsent || false);
        } else {
          router.replace("/");
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
        router.replace("/");
      } finally {
        setLoading(false);
      }
    };
    fetchUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!loading && !state.isLoggedIn) {
      router.replace("/");
    }
  }, [loading, state.isLoggedIn, router]);
  useEffect(() => {
    const usernameChanged = user?.username !== newUsername;
    const marketingConsentChanged = user?.marketingConsent !== newMarketingConsent;
    setChangesMade(usernameChanged || marketingConsentChanged);
  }, [newUsername, newMarketingConsent, dispatch, state, user]);
  const handleUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value
    } = e.target;
    setNewUsername(value);
  }, []);
  const handleMarketingConsent = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      checked
    } = e.target;
    setNewMarketingConsent(checked);
  }, []);
  const validateUsername = async () => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    if (!user?.username || user?.username === newUsername) {
      setIsUsernameValid(true);
      return;
    }
    const isValid = await checkUsernameAvailability(newUsername);
    setIsUsernameValid(isValid);
  };
  const referralCode = state.referralCode;
  const shareUrl = `https://char-gen.com${referralCode ? `?referralCode=${referralCode}` : ""}`;
  const handleSubmit = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();
    await validateUsername();
    try {
      const usernameChanged = user?.username !== newUsername;
      const marketingConsentChanged = user?.marketingConsent !== newMarketingConsent;
      if (usernameChanged || marketingConsentChanged) {
        const updates: UserUpdates = {};
        if (usernameChanged) updates.username = newUsername;
        if (marketingConsentChanged) updates.marketingConsent = newMarketingConsent;
        await updateUser(updates);
        toast.success("Profile updated! Refreshing page and details.", {
          position: "bottom-center"
        });
        setTimeout(() => {
          window.location.reload();
        }, 5000);
      }
    } catch (error) {
      captureException(error, {
        user
      });
      toast.error("Failed to Update!", {
        position: "bottom-center"
      });
      setLoading(false);
    }
  };
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link Copied to Clipboard!", {
      position: "bottom-center"
    });
  };
  return <Styled.Container data-sentry-element="unknown" data-sentry-component="ProfilePage" data-sentry-source-file="index.tsx">
            <Styled.Form onSubmit={handleSubmit} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.Label htmlFor="referralCode" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    Referral Code:
                    <Styled.Icon aria-label="Learn more about Premium Content" onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Image height={24} width={24} priority src={QuestionIcon} alt="Tooltip" data-tooltip-id="referralTip" data-sentry-element="Image" data-sentry-source-file="index.tsx" />

                        <ReferTooltip data-sentry-element="ReferTooltip" data-sentry-source-file="index.tsx" />
                    </Styled.Icon>
                </Styled.Label>
                <Input id="referralCode" name="referralCode" disabled={true} value={user?.referralCode || ""} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
                <Small data-sentry-element="Small" data-sentry-source-file="index.tsx">{shareUrl}</Small>
                <Button variant={ButtonType.PRIMARY} onClick={copyToClipboard} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                    <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">Copy Link</Span>
                </Button>
                <Styled.Label htmlFor="email" data-sentry-element="unknown" data-sentry-source-file="index.tsx">Email:</Styled.Label>
                <Input id="email" name="email" disabled={true} value={user?.email || ""} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
                <Styled.Label htmlFor="username" data-sentry-element="unknown" data-sentry-source-file="index.tsx">Username:</Styled.Label>
                <Input id="username" name="username" onChange={handleUsernameChange} disabled={loading} value={newUsername} minLength={3} maxLength={255} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
                {!isUsernameValid && <Span style={{
        color: colourScheme.error
      }}>
                        This username is taken.
                    </Span>}
                <Styled.Label htmlFor="username" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    Marketing Consent:
                </Styled.Label>
                <Styled.RowContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Toggle id="premiumToggle" checked={newMarketingConsent} onChange={handleMarketingConsent} disabled={loading} data-sentry-element="Toggle" data-sentry-source-file="index.tsx" />
                    <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">
                        {newMarketingConsent ? "Yes, I agree to receive marketing emails" : "No, I do not want to receive marketing emails."}
                    </Span>
                </Styled.RowContainer>
                <Button type="submit" variant={!changesMade ? ButtonType.PRIMARY : ButtonType.SUCCESS} disabled={loading || !isUsernameValid} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                    <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">Update Profile</Span>
                </Button>
                {user?.subscriptionType != null && <Button variant={ButtonType.PRIMARY} disabled={loading || !isUsernameValid} onClick={handleMySubscriptionClick}>
                        <Span>Manage Subscription</Span>
                    </Button>}
            </Styled.Form>
        </Styled.Container>;
};
export default ProfilePage;